// src/pages/IdentityValidationMovil.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import incode from "../services/incode";
import Steps from "../components/Steps";
import FrontId from "../components/FrontId";
import BackId from "../components/BackId";
import ProcessId from "../components/ProcessId";
import Selfie from "../components/Selfie";
import FaceMatch from "../components/FaceMatch";
import RetrySteps from "../components/RetrySteps";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorComponent from "../components/ErrorComponent";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const IdentityValidationMovil = () => {
    const [transactionId, setTransactionId] = useState(null);
    const [token, setToken] = useState(null);
    const [session, setSession] = useState();
    const [step, setStep] = useState(0);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [documentType, setDocumentType] = useState(); // Estado para el tipo de documento

    useEffect(() => {
        // Obtener parámetros una sola vez
        const queryParams = new URLSearchParams(window.location.search);
        const currentTransactionId = queryParams.get("transactionId");
        const currentToken = queryParams.get("token");

        setTransactionId(currentTransactionId);
        setToken(currentToken);

        const fetchTransactionStatus = async () => {
            if (!currentTransactionId || !currentToken) return;

            try {
                setLoading(true);
                const response = await axios.get(
                    `${apiUrl}/api/v1/TransactionValidation/${currentTransactionId}`,
                    { headers: { Authorization: `Bearer ${currentToken}` } }
                );

                if (response.data.status === 0) { 
                    setDocumentType(response.data.documentTypeId);

                    const session = await incode.createSession("ALL", null, {
                        configurationId: response.data.flowId,
                    });
                    await incode.warmup();
                    setSession(session);

                    await axios.post(
                        `${apiUrl}/api/v1/Notificacion/start-validation`,
                        currentTransactionId,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${currentToken}`,
                            },
                        }
                    );
                } else {
                    setError(true);
                    setErrorMessage("Estado de transacción no válido.");
                }
            } catch (err) {
                setError(true);
                setErrorMessage("Error al obtener el estado de la transacción.");
            } finally {
                setLoading(false);
            }
        };

        fetchTransactionStatus();
    }, []); // Dependencias vacías porque los valores se inicializan una vez

    const goNext = () => setStep((prevStep) => prevStep + 1);

    //const handleError = (e) => {
    //    setErrorMessage(e.message || "Error durante el proceso de validación.");
    //    setError(true);
    //};

    const handleError = async (step, errorDetails) => {
        try {
            // Mostrar el error en la interfaz
            setErrorMessage(errorDetails.message || "Ha ocurrido un error en el proceso de validación de identidad.");
            setError(true);
    
            // Clonar el objeto errorDetails sin las imágenes
            const sanitizedErrorDetails = { ...errorDetails };
    
            if (sanitizedErrorDetails.details) {
                sanitizedErrorDetails.details = { ...sanitizedErrorDetails.details };
    
                // Remover las propiedades que contienen imágenes
                delete sanitizedErrorDetails.details.originalImage;
                delete sanitizedErrorDetails.details.frontIdImage;
                delete sanitizedErrorDetails.details.croppedIDFace;
            }
    
            // Enviar el error a la API
            const payload = {
                id: transactionId,
                transactionIncodeId: session?.interviewId,
                transactionIncodeToken: session?.token,
                step,
                errorDetails: JSON.stringify(sanitizedErrorDetails),
            };
    
            await axios.put(`${apiUrl}/api/v1/TransactionValidation/update/error`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (err) {
            console.error("Error al reportar el error a la API:", err);
        }
    };

    const handleProcessCompletion = async (validationResult) => {
        try {
            const payload = {
                id: transactionId,
                resultJson: JSON.stringify(validationResult),
                transactionIncodeId: session?.interviewId,
                transactionIncodeToken: session?.token,
                statusJson: true,
            };

            await axios.put(
                `${apiUrl}/api/v1/TransactionValidation/update`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            goNext();
        } catch (err) {
            console.error("Error al completar el proceso:", err);
            setError(true);
            setErrorMessage("Error al completar el proceso.");
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorComponent message={errorMessage} />;

    return (
        step < (documentType === "passport" ? 5 : 6) ? (
            <Steps currentStep={step}>
                <FrontId session={session} onSuccess={goNext} onError={(e) => handleError("FrontId", { message: e.message, details: e })}  />
                {documentType !== "passport" && (
                    <BackId session={session} onSuccess={goNext} onError={(e) => handleError("BackId", { message: e.message, details: e })}  />
                )}
                <ProcessId session={session} onSuccess={goNext} onError={(e) => handleError("ProcessId", { message: e.message, details: e })} />
                <Selfie session={session} onSuccess={goNext} onError={(e) => handleError("Selfie", { message: e.message, details: e })}  />
                <FaceMatch
                    session={session}
                    onSuccess={() => handleProcessCompletion({})}
                    onError={(e) => handleError("FaceMatch", { message: e.message, details: e })} 
                />
                <RetrySteps session={session} numberOfTries={3} onSuccess={goNext} onError={(e) => handleError("RetrySteps", { message: e.message, details: e })}  />
            </Steps>
        ) : (
            <div>
                <h1 style={{ textAlign: "center" }}>Has finalizado el proceso de validacion de identidad.</h1>
            </div>
        )
    );
};

export default IdentityValidationMovil;
